/**
 * 知识库 - 添加/编辑检查项抽屉
 * UI 严格对齐 web后台.html EditDrawer（line 1582-1728）
 *
 * 字段：
 *  - 检查项名称（编辑时 disabled）
 *  - 覆盖机型（多选 Tag）
 *  - 最低图像数量 / 时间周期（双列）
 *  - 规则列表（动态增删,每条 6 字段;合格判定多行 ≤300,其他单行 ≤40）
 *  - 水印要求（选填,≤300）
 *
 * 与接口（mingyang_knowledge）映射：
 *  - type        ↔ inspection
 *  - models      ↔ machine_model[]
 *  - minPhotos   ↔ media_num.photo
 *  - rules[]
 *    - target    ↔ obj
 *    - spec      ↔ spec
 *    - tol       ↔ uncertainty
 *    - range     ↔ range
 *    - cover     ↔ coverage
 *    - judge     ↔ rule
 *  - watermark   ↔ watermark_rule
 *  - max_wait    ↔ max_wait（最短等待时间,单位秒,默认 300。原"时间周期"
 *                  PERIOD 字段已下线,接口字段统一用 max_wait）
 */
import React, { useEffect, useState } from 'react'
import { Button, Input, InputNumber, Select, Spin, message } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import {
  RuleDetail,
  createKnowledge,
  fetchKnowledgeDetail,
  fetchKnowledgeFilterOptions,
  fetchKnowledgeList,
  updateKnowledge
} from '../api/knowledge'

const { TextArea } = Input
const { Option } = Select

interface EditDrawerProps {
  visible: boolean
  /** 编辑模式传入 knowledgeId */
  knowledgeId?: number
  dispatch: (...args: any[]) => any
  state: any
  onClose: () => void
  onSuccess: () => void
}

/** 最短等待时间默认值(秒) */
const DEFAULT_MAX_WAIT = 300

interface FormState {
  inspection: string
  machine_model: string[]
  min_photos: number
  max_wait: number
  rules: RuleDetail[]
  watermark_rule: string
}

function emptyForm(): FormState {
  return {
    inspection: '',
    machine_model: [],
    min_photos: 3,
    max_wait: DEFAULT_MAX_WAIT,
    rules: [
      { obj: '', spec: '', uncertainty: '', range: '', coverage: '', rule: '' }
    ],
    watermark_rule: ''
  }
}

/** 规则单条的字段配置.
 *  maxLength 默认 40;合格判定是多行 + 上限 300(用户实际填的判定语句较长). */
const RULE_FIELDS: Array<{
  key: keyof RuleDetail
  label: string
  required?: boolean
  maxLength?: number
  multiline?: boolean
}> = [
  { key: 'obj', label: '识别目标', required: true },
  { key: 'spec', label: '规格' },
  { key: 'uncertainty', label: '允许误差' },
  { key: 'range', label: '合格范围' },
  { key: 'coverage', label: '覆盖要求' },
  {
    key: 'rule',
    label: '合格判定',
    required: true,
    maxLength: 300,
    multiline: true
  }
]
const DEFAULT_RULE_FIELD_MAXLEN = 40
/** 水印要求字数上限 */
const WATERMARK_MAXLEN = 300

const EditDrawer: React.FC<EditDrawerProps> = ({
  visible,
  knowledgeId,
  dispatch,
  state,
  onClose,
  onSuccess
}) => {
  // 严格判断 undefined（0 也是有效 id，不当作新增）
  const isNew = knowledgeId === undefined || knowledgeId === null
  const [form, setForm] = useState<FormState>(emptyForm())
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [modelOptions, setModelOptions] = useState<string[]>([])

  useEffect(() => {
    if (!visible) return
    // 拉取整机机型选项：优先用 filter_options 接口；返回空时从 list 反向聚合
    fetchKnowledgeFilterOptions(dispatch)
      .then(async (d) => {
        const fromFilter = d.machine_model_options || []
        if (fromFilter.length > 0) {
          setModelOptions(fromFilter)
          return
        }
        // Fallback：拉一页 list 把所有 item 的 machine_model 合并去重
        try {
          const listRes = await fetchKnowledgeList(dispatch, {
            page: 1,
            page_size: 100
          })
          const set = new Set<string>()
          ;(listRes.list || []).forEach((r: any) => {
            ;(Array.isArray(r.machine_model) ? r.machine_model : []).forEach(
              (m: string) => {
                if (m) set.add(m)
              }
            )
          })
          setModelOptions(Array.from(set).sort())
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        /* no-op */
      })

    if (knowledgeId === undefined || knowledgeId === null) {
      setForm(emptyForm())
      return
    }
    setLoading(true)
    fetchKnowledgeDetail(dispatch, knowledgeId)
      .then((d) => {
        setForm({
          inspection: d.inspection || '',
          machine_model: Array.isArray(d.machine_model) ? d.machine_model : [],
          min_photos: d.media_num?.photo ?? 3,
          max_wait:
            typeof (d as any).max_wait === 'number'
              ? (d as any).max_wait
              : DEFAULT_MAX_WAIT,
          rules:
            (d.rule_details || []).length > 0
              ? d.rule_details.map((r) => ({
                  rule_detail_id: r.rule_detail_id,
                  obj: r.obj || '',
                  spec: r.spec || '',
                  uncertainty: r.uncertainty || '',
                  range: r.range || '',
                  coverage: r.coverage || '',
                  rule: r.rule || ''
                }))
              : emptyForm().rules,
          watermark_rule: d.watermark_rule || ''
        })
      })
      .catch((e) => message.error((e as Error)?.message || '加载详情失败'))
      .finally(() => setLoading(false))
  }, [visible, knowledgeId, dispatch])

  const updateRule = (i: number, key: keyof RuleDetail, val: string) => {
    // 按字段配置的 maxLength cap,默认 40;合格判定 300
    const fieldCfg = RULE_FIELDS.find((f) => f.key === key)
    const cap = fieldCfg?.maxLength ?? DEFAULT_RULE_FIELD_MAXLEN
    if (val.length > cap) return
    setForm((f) => ({
      ...f,
      rules: f.rules.map((r, idx) => (idx === i ? { ...r, [key]: val } : r))
    }))
  }

  const addRule = () =>
    setForm((f) => ({
      ...f,
      rules: [
        ...f.rules,
        {
          obj: '',
          spec: '',
          uncertainty: '',
          range: '',
          coverage: '',
          rule: ''
        }
      ]
    }))

  const removeRule = (i: number) =>
    setForm((f) => ({
      ...f,
      rules: f.rules.filter((_, idx) => idx !== i)
    }))

  const valid =
    form.inspection.trim() &&
    form.machine_model.length > 0 &&
    form.min_photos > 0 &&
    form.rules.length > 0 &&
    form.rules.every((r) => r.obj.trim() && r.rule.trim())

  const handleSubmit = async () => {
    const group_id = state?.group?.groupId || ''
    const user_id = state?.main?.user?.user_id || ''
    if (!group_id || !user_id) {
      message.error('未获取到团队或用户信息')
      return
    }
    setSubmitting(true)
    try {
      const ruleDetails: RuleDetail[] = form.rules.map((r) => ({
        ...(r.rule_detail_id !== undefined
          ? { rule_detail_id: r.rule_detail_id }
          : {}),
        obj: r.obj.trim(),
        spec: r.spec.trim(),
        uncertainty: r.uncertainty.trim(),
        range: r.range.trim(),
        coverage: r.coverage.trim(),
        rule: r.rule.trim()
      }))
      if (isNew) {
        await createKnowledge(dispatch, {
          inspection: form.inspection.trim(),
          machine_model: form.machine_model,
          media_num: { photo: form.min_photos, video: 0 },
          max_wait: form.max_wait,
          watermark_rule: form.watermark_rule.trim(),
          rule_details: ruleDetails,
          group_id,
          user_id
        })
        message.success('检查项已添加')
      } else {
        await updateKnowledge(dispatch, {
          knowledge_id: knowledgeId!,
          inspection: form.inspection.trim(),
          machine_model: form.machine_model,
          media_num: { photo: form.min_photos, video: 0 },
          max_wait: form.max_wait,
          watermark_rule: form.watermark_rule.trim(),
          rule_details: ruleDetails,
          group_id,
          user_id
        })
        message.success('检查项已更新')
      }
      onSuccess()
    } catch (e) {
      message.error((e as Error)?.message || '保存失败')
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible) return null

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,.32)',
          zIndex: 50
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          background: '#F8F9FB',
          zIndex: 51,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-12px 0 32px rgba(15,23,42,.12)'
        }}
      >
        {/* 抽屉头 */}
        <div
          style={{
            padding: '16px 24px',
            background: '#fff',
            borderBottom: '1px solid #EEF0F3',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: '#1F2937'
            }}
          >
            {isNew ? '新增知识' : '编辑知识'}
            {!isNew && (
              <span
                style={{
                  fontSize: 12,
                  color: '#9CA3AF',
                  fontWeight: 400,
                  marginLeft: 8
                }}
              >
                修改后立即生效，无法恢复历史
              </span>
            )}
          </h2>
          <CloseOutlined
            style={{ color: '#9CA3AF', cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        {/* 抽屉主体 */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }}
        >
          <Spin spinning={loading}>
            {/* 检查项名称 */}
            <FieldLabel label="检查项名称" required>
              <Input
                value={form.inspection}
                onChange={(e) => {
                  const v = e.target.value
                  setForm((f) => ({ ...f, inspection: v }))
                }}
                placeholder="如：轮毂变浆轴承螺柱拉伸"
                disabled={!isNew}
                style={
                  !isNew ? { background: '#F1F2F4', color: '#6B7280' } : {}
                }
                maxLength={100}
              />
            </FieldLabel>

            {/* 覆盖机型 — 闭合态仅显示"已选 N 个"，下方独立 Tag 列表可直接删除 */}
            <FieldLabel label="覆盖机型" required hint="可多选，至少选择一个">
              <Select
                mode="multiple"
                className="myse-flat-select"
                value={form.machine_model}
                onChange={(v: string[]) =>
                  setForm((f) => ({ ...f, machine_model: v }))
                }
                placeholder="选择适用的整机机型"
                style={{ width: '100%' }}
                showSearch
                maxTagCount={0}
                maxTagPlaceholder={(omitted: any[]) =>
                  `已选 ${omitted.length} 个`
                }
              >
                {modelOptions.map((m) => (
                  <Option key={m} value={m}>
                    {m}
                  </Option>
                ))}
              </Select>
              {form.machine_model.length > 0 && (
                <div className="myse-tag-list">
                  {form.machine_model.map((m) => (
                    <span key={m} className="myse-tag-list__item">
                      {m}
                      <span
                        className="myse-tag-list__close"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            machine_model: f.machine_model.filter(
                              (x) => x !== m
                            )
                          }))
                        }
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </FieldLabel>

            {/* 最低图像数量 + 最短等待时间 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14
              }}
            >
              <FieldLabel label="最低图像数量" required>
                <InputNumber
                  min={1}
                  value={form.min_photos}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, min_photos: Number(v) || 0 }))
                  }
                  style={{ width: '100%' }}
                />
              </FieldLabel>
              <FieldLabel label="最短等待时间" required>
                <InputNumber
                  min={0}
                  step={1}
                  value={form.max_wait}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      max_wait:
                        typeof v === 'number' && !isNaN(v)
                          ? v
                          : DEFAULT_MAX_WAIT
                    }))
                  }
                  formatter={(v) => `${v} 秒`}
                  parser={(v) => (v ? Number(v.replace(/\D/g, '')) : 0) as any}
                  style={{ width: '100%' }}
                />
              </FieldLabel>
            </div>

            {/* 规则列表 */}
            <FieldLabel
              label="规则"
              required
              hint="每个检查项可含多条规则；每个字段字数 ≤ 40"
            >
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                {form.rules.map((rule, i) => (
                  <div
                    key={i}
                    style={{
                      background: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      padding: 12
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        规则 {i + 1}
                      </span>
                      {form.rules.length > 1 && (
                        <a
                          onClick={() => removeRule(i)}
                          style={{
                            color: '#E84C4C',
                            fontSize: 12,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <CloseOutlined style={{ fontSize: 12 }} /> 删除
                        </a>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10
                      }}
                    >
                      {RULE_FIELDS.map((f) => {
                        const maxLen = f.maxLength ?? DEFAULT_RULE_FIELD_MAXLEN
                        const curLen = String(rule[f.key] || '').length
                        // "合格判定" 多行(textarea),其他字段保持单行
                        const InputCtl = f.multiline ? TextArea : Input
                        return (
                          <FieldLabel
                            key={f.key}
                            label={
                              <span>
                                {f.label}
                                <span
                                  style={{
                                    color: '#9CA3AF',
                                    fontSize: 11,
                                    marginLeft: 4
                                  }}
                                >
                                  {curLen}/{maxLen}
                                </span>
                              </span>
                            }
                            required={f.required}
                            // 多行字段(合格判定)的字数限制大,占两列宽度更舒展
                            style={
                              f.multiline ? { gridColumn: '1 / -1' } : undefined
                            }
                          >
                            <InputCtl
                              value={rule[f.key] as string}
                              onChange={(e: any) =>
                                updateRule(i, f.key, e.target.value)
                              }
                              placeholder={f.label}
                              maxLength={maxLen}
                              {...(f.multiline
                                ? { autoSize: { minRows: 2, maxRows: 5 } }
                                : {})}
                            />
                          </FieldLabel>
                        )
                      })}
                    </div>
                  </div>
                ))}
                <div
                  onClick={addRule}
                  style={{
                    border: '1px dashed #D8DCE3',
                    borderRadius: 8,
                    padding: 10,
                    textAlign: 'center',
                    color: '#3B7DEB',
                    fontSize: 13,
                    cursor: 'pointer',
                    background: '#fff',
                    transition: 'all .15s'
                  }}
                  onMouseEnter={(e) => {
                    ;(e.currentTarget as HTMLDivElement).style.borderColor =
                      '#3B7DEB'
                    ;(e.currentTarget as HTMLDivElement).style.background =
                      '#F5F8FF'
                  }}
                  onMouseLeave={(e) => {
                    ;(e.currentTarget as HTMLDivElement).style.borderColor =
                      '#D8DCE3'
                    ;(e.currentTarget as HTMLDivElement).style.background =
                      '#fff'
                  }}
                >
                  <PlusOutlined
                    style={{ verticalAlign: '-2px', marginRight: 4 }}
                  />
                  添加规则
                </div>
              </div>
            </FieldLabel>

            {/* 水印要求(选填,字数上限 300) */}
            <FieldLabel
              label="水印要求"
              hint={`${form.watermark_rule.length}/${WATERMARK_MAXLEN}`}
            >
              <TextArea
                value={form.watermark_rule}
                onChange={(e) => {
                  const v = e.target.value
                  if (v.length <= WATERMARK_MAXLEN)
                    setForm((f) => ({ ...f, watermark_rule: v }))
                }}
                placeholder="例：必须包含拍摄时间 / 经纬度 / 整机编号(选填)"
                maxLength={WATERMARK_MAXLEN}
                autoSize={{ minRows: 2, maxRows: 5 }}
              />
            </FieldLabel>
          </Spin>
        </div>

        {/* 抽屉底部按钮 */}
        <div
          style={{
            padding: '14px 24px',
            background: '#fff',
            borderTop: '1px solid #EEF0F3',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10
          }}
        >
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            disabled={!valid}
            loading={submitting}
            onClick={handleSubmit}
          >
            {isNew ? '保存并启用' : '保存修改'}
          </Button>
        </div>
      </div>
    </>
  )
}

const FieldLabel: React.FC<{
  label: React.ReactNode
  required?: boolean
  hint?: React.ReactNode
  children: React.ReactNode
  /** 给最外层 wrapper 加额外样式(如 gridColumn 让字段跨列) */
  style?: React.CSSProperties
}> = ({ label, required, hint, children, style }) => (
  <div style={{ marginBottom: 22, ...style }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 8
      }}
    >
      <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
        {required && (
          <span style={{ color: '#E84C4C', marginRight: 3 }}>*</span>
        )}
        {label}
      </span>
      {hint && <span style={{ fontSize: 12, color: '#9CA3AF' }}>{hint}</span>}
    </div>
    {children}
  </div>
)

export default EditDrawer
